import { Body, Composite, Engine, Events, Mouse, MouseConstraint, Query, Vector } from 'matter-js';
import '../components/LassoSpinner';
import { LassoSpinner } from '../components/LassoSpinner';
import { AbstractEntity } from '../entities/AbstractEntity';
import { BubbleEntity } from '../entities/BubbleEntity';
import { ClusterEntity } from '../entities/ClusterEntity';
import { EntityManager } from './EntityManager';

export class InputManager {
    private engine: Engine;
    private entityManager: EntityManager;
    private mouseConstraint: MouseConstraint;
    private container: HTMLElement;
    private marqueeElement: HTMLElement;
    private lassoSpinnerElement: LassoSpinner;

    // Estados
    private isSelecting: boolean = false; // Modo Lasso
    private selectionStart: Vector = { x: 0, y: 0 };
    private lassoInitialStates = new Map<string, { entity: BubbleEntity, initialState: boolean }>();

    // Variables para Click vs Drag
    private dragStartTime: number = 0;
    private dragStartPosition: Vector = { x: 0, y: 0 };
    private clickedEntity: AbstractEntity | null = null;

    // Estados para Long Press (selección/deselección total al completar la animación del spinner)
    private isLongPressPending: boolean = false;
    private longPressStartPos: Vector = { x: 0, y: 0 };

    // Configuración
    private readonly CLICK_THRESHOLD_MS = 200;
    private readonly CLICK_THRESHOLD_DIST = 10;
    private readonly LONG_PRESS_MS = 1000;                  // 1 segundo
    private readonly LONG_PRESS_RADIUS = 15;                // Tolerancia de movimiento para no cancelar el long press

    // Filtro multi-touch: matter-js/Mouse solo lee changedTouches[0] e ignora de qué dedo proviene,
    // por lo que un 2do dedo puede disparar mousedown/mouseup falsos o corromper la posición.
    // Forzamos aquí 1 solo dedo activo (el primero) e ignoramos por completo el resto.
    private activeTouchId: number | null = null;

    // Doble clic/doble-tap: matter-js/Mouse llama preventDefault() en touchstart/touchmove/touchend,
    // lo que suprime la síntesis nativa de click/dblclick del navegador en touch. Por eso se detecta
    // manualmente sobre el mousedown de Matter (unificado para mouse y touch ya filtrado a 1 dedo).
    private lastClickTime: number = 0;
    private lastClickPos: Vector = { x: 0, y: 0 };
    private readonly DOUBLE_CLICK_MS = 350;
    private readonly DOUBLE_CLICK_DIST = 30;

    // Eventos
    private events: IInputManagerEvents = {
        onBubbleClick: (_: BubbleEntity) => { },
        onSelectionChange: (_: BubbleEntity[]) => { },
        onDoubleClick: () => { }
        // onClusterClick: (cluster: ClusterEntity) => { },
    }

    constructor(engine: Engine, container: HTMLElement, entityManager: EntityManager, marqueeElement: HTMLElement, events?: Partial<IInputManagerEvents>) {
        this.engine = engine;
        this.container = container;
        this.entityManager = entityManager;
        this.marqueeElement = marqueeElement;
        if (events) {
            this.events = { ...this.events, ...events };
        }
        this.init();
    }

    private init() {
        // Evita el menú contextual nativo (clic derecho / long-press en touch),
        // que de otro modo compite con el lasso automático y el drag de burbujas.
        this.container.addEventListener('contextmenu', (e) => e.preventDefault());

        // Se registran en `document` (fase de captura) para garantizar que se ejecuten
        // ANTES que los listeners de matter-js/Mouse sobre `container`, sin depender del
        // orden de registro (capture en el mismo nodo destino no tiene prioridad garantizada).
        document.addEventListener('touchstart', this.handleTouchStartFilter, { capture: true, passive: false });
        document.addEventListener('touchmove', this.handleTouchMoveFilter, { capture: true, passive: false });
        document.addEventListener('touchend', this.handleTouchEndFilter, { capture: true, passive: false });
        document.addEventListener('touchcancel', this.handleTouchEndFilter, { capture: true, passive: false });

        this._EnableMouseConstraint();

        // Accesibilidad: permite alternar la selección de una burbuja enfocada con Enter/Espacio
        this.container.addEventListener('keydown', (e) => this.handleKeyDown(e));

        // Indicador visual de long-press: al completar su animación, selecciona/deselecciona todas las burbujas
        this.lassoSpinnerElement = document.createElement('app-lasso-spinner');
        this.lassoSpinnerElement.durationMs = this.LONG_PRESS_MS;
        this.lassoSpinnerElement.setAttribute('aria-hidden', 'true'); // Puramente visual, no debe anunciarse
        this.lassoSpinnerElement.addEventListener('lasso-complete', this.handleLassoComplete);
        this.container.appendChild(this.lassoSpinnerElement);
    }

    /**
     * `container` vive dentro del shadow root de BubbleField; nuestros listeners están en `document`
     * (fuera del shadow), así que `event.target` llega retargeted al host y `container.contains(target)`
     * siempre da false. `composedPath()` sí expone la ruta real a través de shadow roots abiertos.
     */
    private isEventInsideContainer(event: Event): boolean {
        return event.composedPath().includes(this.container);
    }

    /**
     * Detecta doble clic/doble-tap sobre el campo comparando el mousedown actual con el anterior.
     * Se reinicia el rastro tras detectar uno, para no encadenar un 3er click como otro doble-click.
     */
    private detectDoubleClick(pos: Vector) {
        const now = performance.now();
        const dist = Vector.magnitude(Vector.sub(pos, this.lastClickPos));

        if (now - this.lastClickTime < this.DOUBLE_CLICK_MS && dist < this.DOUBLE_CLICK_DIST) {
            this.lastClickTime = 0;
            this.events.onDoubleClick();
            return;
        }

        this.lastClickTime = now;
        this.lastClickPos = { ...pos };
    }

    /**
     * Ignora por completo cualquier dedo que no sea el primero en tocar el campo.
     * El touch original se deja pasar sin tocarlo; los dedos adicionales se bloquean
     * (preventDefault + stopPropagation) para que nunca lleguen a matter-js/Mouse.
     */
    private handleTouchStartFilter = (event: TouchEvent) => {
        if (!this.isEventInsideContainer(event)) return;

        if (this.activeTouchId === null) {
            this.activeTouchId = event.changedTouches[0].identifier; // Primer dedo: se vuelve el único activo
            return;
        }

        // Ya hay un dedo activo: cualquier dedo adicional se ignora por completo
        event.preventDefault();
        event.stopPropagation();
    };

    private handleTouchMoveFilter = (event: TouchEvent) => {
        if (this.activeTouchId === null || !this.isEventInsideContainer(event)) return;

        // matter-js/Mouse solo lee changedTouches[0]; si no pertenece al dedo activo, se bloquea
        if (event.changedTouches[0].identifier !== this.activeTouchId) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    private handleTouchEndFilter = (event: TouchEvent) => {
        if (!this.isEventInsideContainer(event)) return;

        let endedActiveTouch = false;
        for (let i = 0; i < event.changedTouches.length; i++) {
            if (event.changedTouches[i].identifier === this.activeTouchId) {
                endedActiveTouch = true;
                break;
            }
        }

        if (endedActiveTouch) {
            this.activeTouchId = null; // Libera el candado: el siguiente dedo en tocar podrá tomar el control
            return;
        }

        if (this.activeTouchId !== null) {
            // Un dedo secundario se levanta/cancela: no debe afectar la interacción del dedo activo
            event.preventDefault();
            event.stopPropagation();
        }
    };

    /**
     * Maneja la activación por teclado (Enter/Espacio) de una burbuja enfocada,
     * replicando el mismo comportamiento que un clic de mouse.
     */
    private handleKeyDown(event: KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

        const bubbleElement = (event.target as HTMLElement)?.closest?.('.bubble-node') as HTMLElement | null;
        const entity = bubbleElement?._entity;
        if (!(entity instanceof BubbleEntity) || entity._isInCluster || !entity._IsSelectable) return;

        event.preventDefault(); // Evita que Espacio haga scroll de la página

        entity._ToggleSelection();
        this.events.onBubbleClick(entity);
        this.events.onSelectionChange(this.entityManager._GetSelectedBubbles());
    }

    private setupEvents() {
        // 1. EVENTO MOUSEDOWN (El cerebro que decide qué hacer)
        Events.on(this.mouseConstraint, 'mousedown', (event) => {
            if (event.mouse.button === 2) { // Botón derecho
                return;
            }
            const mousePos = event.mouse.position;

            this.detectDoubleClick(mousePos);

            // Verificamos qué tocamos realmente
            const bodies = Composite.allBodies(this.engine.world);
            const hoveredBodies = Query.point(bodies, mousePos);
            const topBody = hoveredBodies[hoveredBodies.length - 1]; // El que está hasta arriba (z-index físico)

            if (topBody) {
                // --- MODO: INTERACCIÓN FÍSICA ---
                this.handleBodyInteractionStart(topBody, mousePos);
            } else {
                // --- INICIO DE LASSO Y DETECCIÓN DE LONG PRESS ---
                this.longPressStartPos = { ...mousePos };
                this.isLongPressPending = true;

                this.showLassoSpinner(mousePos);

                // Inicia el Lasso normal por si el usuario decide arrastrar inmediatamente
                this.startLasso(mousePos);
            }
        });

        // 2. EVENTO MOUSEMOVE (Solo visuales del Lasso)
        Events.on(this.mouseConstraint, 'mousemove', (event) => {
            // Cancelar el Long Press si el usuario se mueve fuera del radio permitido antes de completarse
            if (this.isLongPressPending) {
                const dist = Vector.magnitude(Vector.sub(event.mouse.position, this.longPressStartPos));
                if (dist > this.LONG_PRESS_RADIUS) {
                    this.isLongPressPending = false;
                    this.hideLassoSpinner();
                }
            }

            // Lasso normal
            if (this.isSelecting) {
                this.updateLassoVisuals(event.mouse.position);
            }
        });

        // 3. EVENTO MOUSEUP (Finalizar acciones)
        Events.on(this.mouseConstraint, 'mouseup', (event) => {
            if (this.isLongPressPending) {
                this.isLongPressPending = false;
                this.hideLassoSpinner();
            }

            if (this.isSelecting) {
                // Finalizar lasso normal
                this.endLasso(event.mouse.position);
            } else {
                this.handleBodyInteractionEnd(event.mouse.position);
            }
        });
    }

    private removeEvents() {
        Events.off(this.mouseConstraint, 'mousedown');
        Events.off(this.mouseConstraint, 'mousemove');
        Events.off(this.mouseConstraint, 'mouseup');
    }

    // **************************************************************************
    //#region Public API

    public _GetClickedBubbleEntity(): BubbleEntity | null {
        if (this.clickedEntity instanceof BubbleEntity) {
            return this.clickedEntity;
        }
        return null;
    }

    public _CancelLassoIfActive() {
        if (this.isSelecting) {
            this.endLasso(); // finaliza el lasso con la posición actual
        }
    }

    public _GetPointerPosition(): Vector {
        return this.mouseConstraint.mouse.position;
    }

    /**
     * Libera todos los recursos del InputManager (listeners globales en `document`, mouseConstraint, spinner).
     * Debe llamarse cuando el componente que lo usa se destruye/desmonta.
     */
    public _Destroy(): void {
        document.removeEventListener('touchstart', this.handleTouchStartFilter, { capture: true });
        document.removeEventListener('touchmove', this.handleTouchMoveFilter, { capture: true });
        document.removeEventListener('touchend', this.handleTouchEndFilter, { capture: true });
        document.removeEventListener('touchcancel', this.handleTouchEndFilter, { capture: true });
        this._DisableMouseConstraint();
        this.lassoSpinnerElement?.removeEventListener('lasso-complete', this.handleLassoComplete);
        this.lassoSpinnerElement?.remove();
    }

    /**
     * Desactiva o remueve el mouseConstraint. Si estaba agarrando algo, lo soltará.
     */
    public _DisableMouseConstraint(): void {
        if (this.mouseConstraint) {
            this.removeEvents();
            // Liberar cualquier cuerpo que esté siendo agarrado
            this.mouseConstraint.body = null;
            // Remover el mouseConstraint del mundo
            Composite.remove(this.engine.world, this.mouseConstraint);
            this.mouseConstraint = null;
        }
    }

    /**
     * Activa o agrega el mouseConstraint nuevamente.
     */
    public _EnableMouseConstraint(): void {
        if (!this.mouseConstraint) {
            const mouse = Mouse.create(this.container);
            this.mouseConstraint = MouseConstraint.create(this.engine, {
                mouse: mouse,
                constraint: {
                    stiffness: 0.2, // Rigidez del agarre
                    damping: 0.1,   // Amortiguación para evitar rebotes
                    render: { visible: false }
                }
            });
            Composite.add(this.engine.world, this.mouseConstraint);
            this.setupEvents();
        }
    }

    //#endregion
    // **************************************************************************

    // **************************************************************************
    //#region Interacción con Cuerpos (Click vs Drag, Selección Individual)

    private handleBodyInteractionStart(body: Body, pos: Vector) {
        this.dragStartTime = performance.now();
        this.dragStartPosition = { ...pos };
        this.clickedEntity = body._entity;

        // ** REGLA ANTI-RECURSIVIDAD **
        // si se toca un cluster, no hacer nada, no agrupar, solo arrastrar
        if (this.clickedEntity instanceof ClusterEntity) {
            return;
        }

        // // LÓGICA DE AGRUPACIÓN (Crear cluster al arrastrar selección)
        // if (this.clickedEntity instanceof BubbleEntity) {
        //     this.createClusterFromBubbleSelected(this.clickedEntity); // N/A
        // }
    }

    private handleBodyInteractionEnd(currentPos: Vector) {
        const timeDiff = performance.now() - this.dragStartTime;
        const dist = Vector.magnitude(Vector.sub(currentPos, this.dragStartPosition));

        // DETECCIÓN DE CLICK PURO
        if (timeDiff < this.CLICK_THRESHOLD_MS && dist < this.CLICK_THRESHOLD_DIST) {
            if (this.clickedEntity instanceof BubbleEntity) {
                // Solo permitir seleccionar burbujas que NO están en cluster
                if (!this.clickedEntity._isInCluster) {
                    this.clickedEntity._ToggleSelection();
                    this.events.onBubbleClick(this.clickedEntity);
                    this.events.onSelectionChange(this.entityManager._GetSelectedBubbles());
                }
            }
            else if (this.clickedEntity instanceof ClusterEntity) {
                // logica opcional para seleccionar cluster
                // this.events.onClusterClick(this.clickedEntity);
            }
        }

        this.clickedEntity = null;
    }

    //#endregion
    // **************************************************************************

    // **************************************************************************
    //#region Lasso Logic

    private startLasso(pos: Vector) {
        this.isSelecting = true;
        this.selectionStart = { ...pos };

        // Desactivar colisiones del mouse temporalmente
        this.mouseConstraint.collisionFilter.mask = 0;

        this.lassoInitialStates.clear();

        this.marqueeElement.style.display = 'block';
        this.marqueeElement.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
        this.marqueeElement.style.width = '0px';
        this.marqueeElement.style.height = '0px';
    }

    private updateLassoVisuals(currentPos: Vector) {
        const x = Math.min(this.selectionStart.x, currentPos.x);
        const y = Math.min(this.selectionStart.y, currentPos.y);
        const w = Math.abs(currentPos.x - this.selectionStart.x);
        const h = Math.abs(currentPos.y - this.selectionStart.y);

        this.marqueeElement.style.transform = `translate(${x}px, ${y}px)`;
        this.marqueeElement.style.width = `${w}px`;
        this.marqueeElement.style.height = `${h}px`;

        this.dispatchLassoSelection(currentPos);
    }

    private endLasso(endPos?: Vector) {
        if (!endPos) {
            // Obtener la posición actual del mouse para finalizar el lasso
            const mousePos = this.mouseConstraint.mouse.position;
            endPos = { x: mousePos.x, y: mousePos.y };
        }
        this.isSelecting = false;
        this.marqueeElement.style.display = 'none';

        // RESTAURAR el MouseConstraint (Volver a la máscara por defecto)
        this.mouseConstraint.collisionFilter.mask = 0xFFFFFFFF; // O tu máscara por defecto

        this.dispatchLassoSelection(endPos);

        this.lassoInitialStates.clear();
    }

    /**
     * Al completar la animación del spinner (long-press sostenido sin interrupción),
     * selecciona todas las burbujas si alguna no lo estaba, o las deselecciona si ya estaban todas.
     */
    private handleLassoComplete = () => {
        if (this.entityManager._GetCluster()) {
            return;
        }

        // Cancela el lasso manual pendiente para que el mouseup no recalcule la selección
        if (this.isSelecting) {
            this.isSelecting = false;
            this.marqueeElement.style.display = 'none';
            this.mouseConstraint.collisionFilter.mask = 0xFFFFFFFF;
            this.lassoInitialStates.clear();
        }

        const bodies = Composite.allBodies(this.engine.world);
        const selectableEntities: BubbleEntity[] = [];

        bodies.forEach(body => {
            const entity = body._entity;
            if (entity instanceof BubbleEntity && !entity._isInCluster && entity._IsSelectable) {
                selectableEntities.push(entity);
            }
        });

        // Si al menos una no está seleccionada, seleccionar todas; si ya estaban todas, deseleccionar todas.
        const showSelection = selectableEntities.some(entity => !entity._isSelected);

        selectableEntities.forEach(entity => {
            if (entity._isSelected !== showSelection) {
                entity._ToggleSelection(showSelection);
            }
        });

        this.events.onSelectionChange(this.entityManager._GetSelectedBubbles());
    };

    //#endregion
    // **************************************************************************

    // **************************************************************************
    //#region spinner

    private showLassoSpinner(pos: Vector) {
        this.lassoSpinnerElement.show(pos.x, pos.y);
    }

    private hideLassoSpinner() {
        this.lassoSpinnerElement.hide();
    }

    //#endregion
    // **************************************************************************

    // **************************************************************************

    private dispatchLassoSelection(endPos: Vector) {
        // Calcular área y seleccionar (igual que antes)
        const xMin = Math.min(this.selectionStart.x, endPos.x);
        const xMax = Math.max(this.selectionStart.x, endPos.x);
        const yMin = Math.min(this.selectionStart.y, endPos.y);
        const yMax = Math.max(this.selectionStart.y, endPos.y);

        const bodies = Composite.allBodies(this.engine.world);
        const enclosedBodies = Query.region(bodies, { min: { x: xMin, y: yMin }, max: { x: xMax, y: yMax } });

        // Identificar entidades actualmente DENTRO del Lasso
        const enclosedEntities = new Map<string, BubbleEntity>();

        enclosedBodies.forEach(body => {
            const entity = body._entity;
            if (entity instanceof BubbleEntity && !entity._isInCluster && entity._IsSelectable) {
                enclosedEntities.set(entity._id, entity);

                // Si es la PRIMERA VEZ que el lasso toca esta burbuja en este arrastre,
                // guardamos una copia inmutable de su estado original.
                if (!this.lassoInitialStates.has(entity._id)) {
                    this.lassoInitialStates.set(entity._id, {
                        entity: entity,
                        initialState: entity._isSelected
                    });
                }
            }
        });

        // Determinar la acción general (showSelection)
        // Regla: Buscamos en el CACHÉ ORIGINAL, ignoramos el estado actual.
        let showSelection = false;
        enclosedEntities.forEach((_entity, id) => {
            const record = this.lassoInitialStates.get(id);
            if (record && record.initialState === false) {
                showSelection = true; // Si al menos una originalmente NO estaba seleccionada, el lasso selecciona
            }
        });

        // Aplicar el estado calculado a las burbujas dentro del Lasso
        enclosedEntities.forEach(entity => {
            if (entity._isSelected !== showSelection) {
                entity._ToggleSelection(showSelection); // Forzamos true o false
            }
        });

        // Restaurar el estado de las burbujas que SALIERON del Lasso
        // Iteramos sobre el historial de todo lo que se ha tocado
        this.lassoInitialStates.forEach((record, id) => {
            // Si la burbuja está en el historial, pero ya no está en el recuadro actual...
            if (!enclosedEntities.has(id)) {
                // ... la restauramos exactamente a como estaba antes de tocarla con el lasso
                if (record.entity._isSelected !== record.initialState) {
                    record.entity._ToggleSelection(record.initialState);
                }
                // La eliminamos del historial para que si volvemos a pasar el recuadro sobre ella, se evalúe como una burbuja nueva.
                this.lassoInitialStates.delete(id);
            }
        });

        // Notificar el cambio de selección
        this.events.onSelectionChange(this.entityManager._GetSelectedBubbles());
    }
}

export interface IInputManagerEvents {
    onBubbleClick: (bubble: BubbleEntity) => void;
    onSelectionChange: (selectedBubbles: BubbleEntity[]) => void;
    onDoubleClick: () => void;
    // onClusterClick: (cluster: ClusterEntity) => void;
}